import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useReportsStore } from '../../stores/reportsStore';
import { useReportTemplates, useReportMetrics, useGenerateReport } from '../../hooks/useReportsData';
import { ReportTemplateCard } from './ReportTemplateCard';
import { ReportFormat, ReportMetric } from '../../types/reports.types';
import { cn } from '../../lib/utils';

interface ReportBuilderWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ReportBuilderWizard: React.FC<ReportBuilderWizardProps> = ({ isOpen, onClose }) => {
  const { data: templates, isLoading: templatesLoading } = useReportTemplates();
  const { data: metrics, isLoading: metricsLoading } = useReportMetrics();
  const { mutate: generateReport, isPending } = useGenerateReport();
  
  const { step, reportType, filters, format, reportName, setStep, setReportType, setFilters, setFormat, setReportName, resetBuilder } = useReportsStore();
  
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedNetworks, setSelectedNetworks] = useState<string[]>([]);
  const [currency, setCurrency] = useState('USD');
  
  useEffect(() => {
    if (!isOpen) {
      resetBuilder();
      setSelectedMetrics([]);
      setDateFrom('');
      setDateTo('');
      setSelectedNetworks([]);
      setCurrency('USD');
    }
  }, [isOpen, resetBuilder]);
  
  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };
  
  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };
  
  const handleGenerate = () => {
    if (!reportType || !reportName) return;
    
    generateReport({
      name: reportName,
      type: reportType,
      format,
      filters: {
        dateFrom,
        dateTo,
        networkIds: selectedNetworks,
        currencyId: currency,
        metrics: selectedMetrics,
      },
    }, {
      onSuccess: () => {
        onClose();
      },
    });
  };
  
  const canProceed = () => {
    switch (step) {
      case 1: return !!reportType;
      case 2: return dateFrom && dateTo;
      case 3: return true;
      case 4: return reportName && selectedMetrics.length > 0;
      default: return false;
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create Report</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Step {step} of 4</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={cn(
                  'h-2 flex-1 rounded-full transition-colors',
                  s <= step ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'
                )}
              />
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Select Report Type</h3>
              {templatesLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} className="h-40 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {templates?.map(template => (
                    <ReportTemplateCard
                      key={template.id}
                      template={template}
                      selected={reportType === template.type}
                      onClick={() => {
                        setReportType(template.type);
                        setSelectedMetrics(template.defaultMetrics);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          
          {step === 2 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Select Date Range</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">From Date</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">To Date</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                  />
                </div>
              </div>
            </div>
          )}
          
          {step === 3 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Filter by Network (Optional)</h3>
              <div className="space-y-2">
                {['BetNetwork', 'CasinoHub', 'PokerPro', 'SlotMaster'].map((network, idx) => (
                  <label key={network} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                    <input
                      type="checkbox"
                      checked={selectedNetworks.includes(`net_${idx + 1}`)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedNetworks([...selectedNetworks, `net_${idx + 1}`]);
                        } else {
                          setSelectedNetworks(selectedNetworks.filter(id => id !== `net_${idx + 1}`));
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span>{network}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Report Name</label>
                <input
                  type="text"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  placeholder="e.g., Q4 Performance Report"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Currency</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="BTC">BTC</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Metrics</label>
                {metricsLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => <div key={i} className="h-10 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />)}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {metrics?.map((metric: ReportMetric) => (
                      <label key={metric.id} className="flex items-center gap-2 p-2 border border-gray-200 dark:border-gray-700 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                        <input
                          type="checkbox"
                          checked={selectedMetrics.includes(metric.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMetrics([...selectedMetrics, metric.id]);
                            } else {
                              setSelectedMetrics(selectedMetrics.filter(m => m !== metric.id));
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{metric.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Export Format</label>
                <div className="flex gap-4">
                  {(['csv', 'xlsx', 'pdf', 'json'] as ReportFormat[]).map(f => (
                    <label key={f} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="format"
                        checked={format === f}
                        onChange={() => setFormat(f)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm uppercase">{f}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={handleBack}
            disabled={step === 1}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              Cancel
            </button>
            
            {step < 4 ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex items-center gap-2 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleGenerate}
                disabled={!canProceed() || isPending}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isPending ? 'Generating...' : 'Generate Report'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
